import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  ChangeEvent,
} from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import filesize from 'filesize';
import * as Yup from 'yup';

import { ReactComponent as AlertIcon } from '../../../assets/icons/ubr_alert.svg';
import { Layout, Button, Input, Select } from '../../../components';
import SelectType from '../../../components/Form/Select/select_type';
import Upload from '../../../components/Upload';
import FileList from '../../../components/Upload/FileList';
import { useToast } from '../../../hooks/toast';
import { RoutesPaths } from '../../../routes';
import api from '../../../services/api';
import type { ManualDataDTO } from '../../../shared/interfaces/manual';
import type { ParamsProps } from '../../../shared/interfaces/parameter';
import type { ScreenData } from '../../../shared/interfaces/screen';
import getValidationErrors from '../../../utils/getValidationErrors';
import {
  Container,
  FormContainer,
  ImportFileContainer,
  FooterContainer,
  Label,
} from './styles';

interface FileProps {
  file?: File | any;
  name: string;
  readableSize: string;
}

const Editar: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  let [hasProgress, setHasProgress] = useState<any>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  let [isLoading, setIsLoading] = useState(true);
  const formRef = useRef<FormHandles | null>(null);
  const history = useHistory();

  const [isLoadingInput, setIsLoadingInput] = useState<boolean>(true);
  const location = useLocation();
  const [uploadedFiles, setUploadedFiles] = useState<FileProps[] | any>([]);
  const { id } = useParams<ParamsProps>();
  const { addToast } = useToast();
  const [dataTable, setDataTable] = useState<SelectType[]>([]);
  const [data, setData] = useState<ManualDataDTO>({
    id: '',
    file: '',
    readableSize: '',
    tituloArquivo: '',
    nomeManual: '',
    listaDeTelas: [],
    nomeRepositorio: '',
    identificadorArquivoRepositorio: '',
  });
  let selectedScreens = useRef<number[] | null>(null);

  useEffect(() => {
    if (id !== undefined) {
      findManual(id);
    }
  }, [id]);

  const fetchData = useCallback(async () => {
    try {
      const response = await api.get<ScreenData[]>(`/tela`);
      setIsLoadingInput(false);
      let screenValues: SelectType[] = response.data.map(
        (screen: ScreenData) => {
          return {
            value: screen.id,
            label: screen.titulo,
          };
        },
      );

      setDataTable(screenValues);
    } catch (e) {
      console.error(e);
    }
  }, []);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function updateValue(e: ChangeEvent<HTMLInputElement>) {
    setData({
      ...data,
      [e.target.name]: e.target.value,
    });
  }

  const handleSubmit = useCallback(
    async (data: any) => {
      setIsLoading(true);
      const { file } = uploadedFiles[0];

      const telas = JSON.stringify(
        (data.listaDeTelas = selectedScreens.current),
      );

      const dataFile = new FormData();

      if (uploadedFiles[0].file) {
        dataFile.append('file', file, file.name);
        dataFile.append('nomeManual', file.name);
      }

      dataFile.append('nomeRepositorio', 'manuais');
      dataFile.append('tituloArquivo', data.tituloArquivo);
      dataFile.append('listaDeTelas', telas.replace(/]|[[]|"/g, ''));

      try {
        if (id !== undefined) {
          if (!data.listaDeTelas?.length) {
            const schemaSelect = Yup.object().shape({
              tituloArquivo: Yup.string()
                .max(40, 'No máximo 40 caracteres')
                .matches(
                  /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
                  'Um ou mais caracteres digitados não são permitidos!',
                )
                .required('Este campo é obrigatório!'),
              select: Yup.string().required('Este campo é obrigatório!'),
            });
            await schemaSelect.validate(data, {
              abortEarly: false,
            });
          }
          const schema = Yup.object().shape({
            tituloArquivo: Yup.string()
              .max(200, 'No máximo 200 caracteres')
              .matches(
                /^(?!\s*$)[-a-zA-Z0-9_:,.' ']/,
                'Um ou mais caracteres digitados não são permitidos!',
              )
              .required('Este campo é obrigatório!'),
          });

          await schema.validate(data, {
            abortEarly: false,
          });

          await api.post(`/manual/${id}`, dataFile, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (event: any) => {
              let progress: number = Math.round(
                (event.loaded * 100) / event.total,
              );
              setHasProgress(progress);
            },
          });

          setIsLoading(false);

          addToast({
            type: 'success',
            title: 'Manual editado com sucesso!',
            description: 'As informações do Manual foram alteradas.',
          });
        }

        setTimeout(() => {
          history.push(RoutesPaths.listManual);
        }, 4000);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errorMessages = getValidationErrors(err);
          formRef.current?.setErrors(errorMessages);
          return;
        } else {
          console.error(err);
          addToast({
            type: 'error',
            title: 'Manual já cadastrado!',
            description:
              'As informações que foram inseridas, já estão presentes no sistema.',
          });
        }
      }
    },
    [addToast, history, uploadedFiles, id],
  );

  async function findManual(id: string) {
    const response = await api.get<ManualDataDTO>(`/manual/${id}`);
    setData({
      id: id,
      nomeManual: response.data.nomeManual,
      tituloArquivo: response.data.tituloArquivo,
      file: response.data.file,
      listaDeTelas: response.data.listaDeTelas,
      readableSize: response.data.readableSize,
      nomeRepositorio: response.data.nomeRepositorio,
      identificadorArquivoRepositorio:
        response.data.identificadorArquivoRepositorio,
    });

    const uploadedFile: FileProps = {
      file: response.data.file,
      name: response.data.nomeManual,
      readableSize: response.data.readableSize,
    };

    setUploadedFiles([uploadedFile]);

    selectedScreens.current = response.data.listaDeTelas;
  }

  function handleScreenChange(dataTable: any | null) {
    let screens: number[] = [];
    if (dataTable) {
      screens = dataTable.map((item: any) => item['value']);
    }

    const newData = data;
    newData.listaDeTelas = screens;

    selectedScreens.current = screens;
    setData(newData);
  }

  function back() {
    if (location.pathname.match('/listar')) {
      history.push(RoutesPaths.home);
    } else {
      history.goBack();
    }
  }

  function submitFile(files: File[]): void {
    const fileUpload = files.map(file => ({
      file,
      name: file.name,
      readableSize: filesize(file.size),
    }));
    setUploadedFiles(fileUpload);
  }

  const removeFile = () => {
    // setFileUpload('');
    setUploadedFiles('');
  };

  return (
    <>
      <Layout title={'Editar Manual'} hideFloatingButton>
        <Container>
          <FormContainer>
            <Form onSubmit={handleSubmit} ref={formRef}>
              <Input
                type="text"
                label="Manual"
                name="tituloArquivo"
                value={data.tituloArquivo}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateValue(e)}
                loading={isLoadingInput}
              />

              <Select
                name={'select'}
                options={dataTable}
                onChange={handleScreenChange}
                placeholder="Selecione"
                initialValues={data.listaDeTelas}
                label="Telas"
                loading={!dataTable.length}
              />

              <ImportFileContainer>
                <Label>Arquivos</Label>
                {!!uploadedFiles.length && (
                  <FileList files={uploadedFiles} handleErase={removeFile} />
                )}
                <Upload onUpload={submitFile} />
                <FooterContainer>
                  <div>
                    <p>
                      <AlertIcon />
                      Limite máximo de 5mb
                    </p>
                  </div>
                  <div>
                    <Button
                      variant="solid"
                      style={{ marginTop: '1rem' }}
                      type="submit"
                      disabled={!uploadedFiles.length}
                    >
                      Adicionar
                    </Button>
                    <Button
                      variant="outline"
                      color={'#C71F37'}
                      style={{ marginTop: '1rem' }}
                      onClick={() =>
                        setTimeout(() => {
                          back();
                        }, 500)
                      }
                    >
                      Cancelar
                    </Button>
                  </div>
                </FooterContainer>
              </ImportFileContainer>
            </Form>
          </FormContainer>
        </Container>
      </Layout>
    </>
  );
};

export default Editar;
